// src/stores/callStore.js
import { defineStore } from 'pinia'
import { ref, computed, watch } from 'vue'
import { db, functions } from '@/services/firebaseconfig'
import {
  doc,
  updateDoc,
  setDoc,
  getDocs,
  collection,
  query,
  where,
  orderBy,
  limit,
  serverTimestamp,
  runTransaction,
} from 'firebase/firestore'
import { onSnapshot } from '@/services/sessionListeners'
import { httpsCallable } from 'firebase/functions'
import { useUserStore } from './userStore'
import { useAlertPreferencesStore } from '@/stores/alertPreferencesStore'
import { voiceCallService } from '@/services/VoiceCallService'
import { publishActiveCall } from '@/services/activeCallTabCoordinator'
import { registerRefreshBlocker } from '@/services/backgroundRefresh'
import { playRingtone } from '@/utils/audioTones'
import { ensureMicrophonePermission } from '@/utils/micPermission'
import { liveCallCallable } from '@/utils/voiceCallPath'
import { eventBus } from '@/utils/eventBus'
import { companyInvitationJoinParams } from '@/utils/nativeCompanyInvitation'

export const useCallStore = defineStore('callStore', () => {
  const userStore = useUserStore()

  // -------------------------
  // State
  // -------------------------
  const isAvailable = ref(false) // local toggle state
  const isRinging = ref(false)
  const isConnecting = ref(false) // true while answerCall is in-flight (shared across components)
  const myActiveCallSid = ref(null) // callSid of the call this agent answered (conference model)
  // The voice connection each answered call joined the room on (callSid → connection), so the store can hang up its OWN
  // leg when the caller it accepted never reaches the room (bug I0zL42wYNHOFn9dzELBc), never another call's.
  const answeredLegs = new Map()
  const invitationIntents = new Map()
  function invitationIntent(kind, callSid, targetUid = '') {
    const uid = userStore.user?.uid
    if (!uid) throw new Error('Sign in before using call controls')
    const key = JSON.stringify([kind, uid, callSid, targetUid]),
      now = Date.now()
    for (const [id, intent] of invitationIntents)
      if (intent.expires <= now) invitationIntents.delete(id)
    if (!invitationIntents.has(key))
      invitationIntents.set(key, { id: crypto.randomUUID(), expires: now + 120000 })
    return { key, uid, id: invitationIntents.get(key).id }
  }
  watch(
    () => userStore.user?.uid,
    () => invitationIntents.clear()
  )
  // One-click dial (Phase 97 S2): any screen asks for a call here; the app-wide CallPanel, which owns the one dialer,
  // places it. A dialer created inside a list row would hang the call up when the row unmounts (useDialer onUnmounted).
  const dialRequest = ref(null) // { phone, customerId, customerName, nonce }

  const heartbeatInterval = ref(null)
  let sessionUnsub = null
  // Wake re-stamp listeners (visibility/focus/online → immediate heartbeat)
  let wakeListenersAttached = false
  let removeWakeListeners = null

  // -------------------------
  // Computed
  // -------------------------
  const callStatus = computed(() => {
    const session = activeCallSession.value
    if (!session) return 'idle'

    // Combine status and channel into legacy format for backward compatibility
    // Backend stores: { status: 'ringing', channel: 'browser' }
    // Frontend expects: 'ringing_browser'
    if (session.channel) {
      return `${session.status}_${session.channel}`
    }

    return session.status || 'idle'
  })

  const callerInfo = computed(() => ({
    name: activeCallSession.value?.customerName || 'Unknown Caller',
    number: activeCallSession.value?.fromNumber || activeCallSession.value?.from || 'Unknown',
    customerId: activeCallSession.value?.customerId || null,
  }))

  // -------------------------
  // Actions
  // -------------------------

  /**
   * 1) Initialize Heartbeat & Availability Sync
   */
  async function initPresence() {
    if (!userStore.user?.uid) return

    // Request OS-level notification permission once per user. This is the
    // audio fallback for inbound calls when the page hasn't received a recent
    // user gesture (browser autoplay policy blocks AudioContext in that case).
    ensureNotificationPermission()

    // Sync initial local state from user profile.
    // First-time users have no `availableForCalls` field at all, which makes
    // the backend `where('availableForCalls', '==', true)` filter exclude them
    // even AFTER they toggle it (the toggle correctly writes `true`, but until
    // they do, they're invisible to inbound ring routing). Stamp `false` once
    // so the field exists; the user opts in by clicking the toggle.
    if (userStore.user.availableForCalls !== undefined) {
      isAvailable.value = !!userStore.user.availableForCalls
    } else {
      isAvailable.value = false
      try {
        await updateDoc(doc(db, 'users', userStore.user.uid), { availableForCalls: false })
      } catch (e) {
        console.warn(
          '[CallStore] Failed to stamp availableForCalls=false for new user:',
          e?.message || e
        )
      }
    }

    if (heartbeatInterval.value) clearInterval(heartbeatInterval.value)

    let lastHeartbeatAt = 0
    const sendHeartbeat = async () => {
      if (!userStore.user?.uid) return
      lastHeartbeatAt = Date.now()
      try {
        await updateDoc(doc(db, 'users', userStore.user.uid), { lastSeen: serverTimestamp() })
      } catch (e) {
        console.warn('[CallStore] Heartbeat failed:', e?.code || e?.message || e)
      }
    }

    await sendHeartbeat()

    // Initial Schedule Check
    checkScheduleAndAutoToggle()

    // Loop
    heartbeatInterval.value = setInterval(() => {
      sendHeartbeat()
      checkScheduleAndAutoToggle()
    }, 60000)

    // Ring eligibility requires lastSeen within voicePresenceWindowMs (5 min).
    // A naked interval stops during OS sleep and lags on wake, silently aging
    // the agent out of ringTargets — re-stamp immediately when the tab comes
    // back (visible/focus/online), throttled so tab-flipping doesn't spam
    // writes. Listeners are detached in stop().
    const wakeHeartbeat = () => {
      if (typeof document !== 'undefined' && document.hidden) return
      if (Date.now() - lastHeartbeatAt < 30_000) return
      sendHeartbeat()
      checkScheduleAndAutoToggle()
    }
    if (!wakeListenersAttached) {
      wakeListenersAttached = true
      document.addEventListener('visibilitychange', wakeHeartbeat)
      window.addEventListener('focus', wakeHeartbeat)
      window.addEventListener('online', wakeHeartbeat)
      removeWakeListeners = () => {
        document.removeEventListener('visibilitychange', wakeHeartbeat)
        window.removeEventListener('focus', wakeHeartbeat)
        window.removeEventListener('online', wakeHeartbeat)
        wakeListenersAttached = false
        removeWakeListeners = null
      }
    }
  }

  /**
   * Helper: Check Schedule and Auto-Switch Availability
   * Logic:
   * - If no schedule: Do nothing.
   * - If transition detected (In -> Out or Out -> In): Update Toggle.
   */
  // Track previous state to detect boundary crossings
  let wasInWindow = null

  function checkScheduleAndAutoToggle() {
    const user = userStore.user
    if (!user?.uid) return

    const schedule = user.availabilityWindows
    // If no schedule set, manual toggle rules apply exclusively.
    if (!schedule || Object.keys(schedule).length === 0) return

    const now = new Date()
    const tz = schedule.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone

    let isInWindow = false

    try {
      // Get formatting in User TZ
      const fmt = new Intl.DateTimeFormat('en-US', {
        timeZone: tz,
        hour12: false,
        hour: '2-digit',
        minute: '2-digit',
        weekday: 'long',
      })
      const parts = fmt.formatToParts(now)
      const part = type => parts.find(p => p.type === type)?.value

      const currentTimeStr = `${part('hour')}:${part('minute')}` // "09:00"
      const dayKey = part('weekday').toLowerCase() // "monday"

      // Check Day
      const ranges = schedule[dayKey] || []

      // Check Ranges
      isInWindow = ranges.some(r => currentTimeStr >= r.start && currentTimeStr <= r.end)

      // TODO: Add Exception date checking here if you need holiday logic on user-level schedules
    } catch (e) {
      console.warn('[CallStore] Schedule check failed', e)
      return
    }

    // Logic:
    // 1. On Load (wasInWindow === null): Enforce Schedule?
    //    Prompt: "toggled on if... logged in and within window".
    //    Let's enforce synchronization on load to ensure compliance.
    // 2. Boundary Crossing:
    //    Out -> In: Turn ON
    //    In -> Out: Turn OFF

    const currentToggle = isAvailable.value

    if (wasInWindow === null) {
      // Initial Load Logic
      if (isInWindow && !currentToggle) {
        console.log('[CallStore] Schedule Enforced: Auto-setting Available')
        toggleAvailability()
      } else if (!isInWindow && currentToggle) {
        // Optional: Auto-off on load if outside window?
        // Risk: User logged in to work late and gets kicked off.
        // Let's only auto-on.
        // Actually prompt says: "automatically toggled off if... outside".
        console.log('[CallStore] Schedule Enforced: Auto-setting Unavailable')
        toggleAvailability()
      }
    } else {
      // Boundary Crossing Logic
      if (!wasInWindow && isInWindow) {
        // Just started shift
        if (!currentToggle) {
          console.log('[CallStore] Schedule Start: Auto-setting Available')
          toggleAvailability()
        }
      } else if (wasInWindow && !isInWindow) {
        // Just ended shift
        if (currentToggle) {
          console.log('[CallStore] Schedule End: Auto-setting Unavailable')
          toggleAvailability()
        }
      }
    }

    wasInWindow = isInWindow
  }

  /**
   * 2) Toggle Availability
   */
  async function toggleAvailability() {
    if (!userStore.user?.uid) return

    const newVal = !isAvailable.value
    isAvailable.value = newVal // optimistic

    try {
      await updateDoc(doc(db, 'users', userStore.user.uid), {
        availableForCalls: newVal,
      })
      // Sync public chat availability for consumer widget
      syncChatAvailability()
    } catch (e) {
      isAvailable.value = !newVal
      console.error('[CallStore] Failed to toggle availability:', e)
    }
  }

  /**
   * Sync public publicConfig/chatAvailability for the consumer chat widget.
   * Checks if any agent is available and writes a publicly-readable doc.
   */
  async function syncChatAvailability() {
    try {
      const q = query(collection(db, 'users'), where('availableForCalls', '==', true), limit(1))
      const snap = await getDocs(q)
      await setDoc(
        doc(db, 'publicConfig', 'chatAvailability'),
        {
          online: !snap.empty,
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      )
    } catch (e) {
      console.warn('[CallStore] Failed to sync chat availability:', e.message)
    }
  }

  // -------------------------
  // State: Multiple Sessions
  // -------------------------
  const sessions = ref([]) // Array of all relevant sessions

  // Computed: Waiting Queue
  const waitingQueue = computed(() => {
    return sessions.value
      .filter(s => s.status === 'waiting')
      .sort((a, b) => {
        // Oldest waiting first
        const ta = a.waitingSince?.toMillis?.() || 0
        const tb = b.waitingSince?.toMillis?.() || 0
        return ta - tb
      })
  })

  // Computed: Active Calls (Ringing or Connected)
  const activeCalls = computed(() => {
    return sessions.value
      .filter(s => ['ringing', 'connected', 'forwarding', 'timeout_ivr'].includes(s.status))
      .sort((a, b) => {
        // Newest first
        const ta = a.createdAt?.toMillis?.() || 0
        const tb = b.createdAt?.toMillis?.() || 0
        return tb - ta
      })
  })

  // "My active call" — session I'm involved with (ringing for me, or I answered)
  const activeCallSession = computed(() => {
    const myUid = userStore.user?.uid

    // Priority 1: Session I answered (tracked by myActiveCallSid — conference model)
    if (myActiveCallSid.value) {
      const match = sessions.value.find(s => s.callSid === myActiveCallSid.value)
      if (match) return match
    }

    // Priority 2: Session where I'm the answeredByUid (covers queue pickup + reconnect)
    if (myUid) {
      const mySession = sessions.value.find(
        s => s.answeredByUid === myUid && ['ringing', 'connected', 'timeout_ivr'].includes(s.status)
      )
      if (mySession) return mySession
    }

    // Priority 3: Outbound call via dialer (voiceCallService.currentConnection)
    if (voiceCallService.currentConnection && !myActiveCallSid.value) {
      const sid = voiceCallService.currentConnection.parameters?.CallSid
      if (sid) {
        const match = sessions.value.find(s => s.callSid === sid)
        if (match) return match
      }
    }

    // Fallback: first ringing browser session targeting me, or first session
    if (myUid) {
      const myRinging = sessions.value.find(
        s =>
          s.status === 'ringing' &&
          s.channel === 'browser' &&
          (s.ringTargets || []).some(t => t.uid === myUid)
      )
      if (myRinging) return myRinging
    }

    return (
      sessions.value.find(s => s.status === 'ringing' && s.channel === 'browser') ||
      sessions.value[0] ||
      null
    )
  })

  // Computed to check if user owns any active connected call
  const myActiveCall = computed(() => {
    const myUid = userStore.user?.uid
    if (!myUid) return null

    // Check by myActiveCallSid (conference model)
    if (myActiveCallSid.value) {
      return (
        sessions.value.find(s => s.callSid === myActiveCallSid.value && s.status === 'connected') ||
        null
      )
    }

    // Check if we're the answeredByUid on a connected call
    return sessions.value.find(s => s.answeredByUid === myUid && s.status === 'connected') || null
  })

  // The background cache refresher never reloads this tab while it is on a call
  // (src/services/backgroundRefresh.js); same condition as the beforeunload prompt below.
  registerRefreshBlocker('active-call', () => !!myActiveCall.value)

  // Cross-tab coordination: this tab broadcasts ownership of an active call so
  // other tabs can avoid re-registering the Twilio Voice Device (which would
  // kick our registration out and drop audio). Cleared as soon as the call
  // ends. Also gates the beforeunload warning below.
  watch(
    () => myActiveCall.value?.callSid || null,
    sid => {
      publishActiveCall(sid)
    },
    { immediate: true }
  )

  // Native browser confirm when the user tries to close the tab during an
  // active call. Modern browsers ignore the custom message but still show
  // their own "Leave site?" prompt as long as we set returnValue.
  if (typeof window !== 'undefined') {
    window.addEventListener('beforeunload', e => {
      if (myActiveCall.value) {
        e.preventDefault()
        e.returnValue = 'You are on an active call. Leaving will end the call.'
        return e.returnValue
      }
    })
  }

  /**
   * 3) Listen for Active Call Sessions (Persistent Panel Logic)
   */
  // Ring-freshness guard (bug bhn23qDNTIHeHKnmQjxL, Evelina 2026-08-27: reloading the portal rang
  // for calls that ended hours earlier). The first snapshot after (re)subscribing carries every
  // session that is STILL 'ringing', including orphaned invites that never reached a terminal
  // state. Those are not new calls: a ring that began more than RING_MAX_AGE_MS ago is an orphan,
  // and a ring that was already there when the page loaded is only fresh if it began seconds ago.
  const RING_MAX_AGE_MS = 2 * 60 * 1000
  const FRESH_ON_LOAD_MS = 30 * 1000
  let sessionsFirstSnapshotSeen = false
  const ringKeysSeen = new Set()

  function sessionStartedMs(s) {
    const created = s?.createdAt?.toDate?.()
    if (created instanceof Date && !Number.isNaN(created.getTime())) return created.getTime()
    if (typeof s?.createdAt === 'number') return s.createdAt
    if (typeof s?.createdAt === 'string') {
      const t = new Date(s.createdAt).getTime()
      return Number.isNaN(t) ? null : t
    }
    return null
  }

  // Is session `s` ringing for `uid` right now? Two ringing scenarios target a user:
  //   1) Inbound call: session.status === 'ringing' AND uid is in ringTargets[]
  //   2) Invite-to-Call: session.status === 'connected' AND
  //      participants.agent_<uid>.status === 'ringing'
  // One predicate for the snapshot's ringtone and for declineCallInvite, so declining an
  // invite never silences another call that is still ringing for the same person.
  function ringsFor(s, uid, now) {
    if (!uid) return false
    const isInbound =
      s.status === 'ringing' &&
      s.channel === 'browser' &&
      (s.ringTargets || []).some(t => t.uid === uid)
    const isInvite =
      s.status === 'connected' && s.participants?.[`agent_${uid}`]?.status === 'ringing'
    if (!isInbound && !isInvite) return false
    // An inbound ring older than the ring timeout can only be an orphaned invite
    // (a lost conference-end webhook); it is not ringing for anyone.
    if (isInbound && now - sessionStartedMs(s) > RING_MAX_AGE_MS) return false
    return true
  }

  function subscribeToCallSessions() {
    if (sessionUnsub) sessionUnsub()
    sessionsFirstSnapshotSeen = false
    ringKeysSeen.clear()

    console.log('[CallStore] Subscribing to active sessions...')

    // Subscribe to recent active sessions
    const q = query(
      collection(db, 'activeCallSessions'),
      where('status', 'in', [
        'ringing',
        'waiting',
        'connected',
        'forwarding',
        'voicemail',
        'ended',
        'timeout_ivr',
      ]),
      orderBy('createdAt', 'desc'),
      limit(50)
    )

    sessionUnsub = onSnapshot(
      q,
      snap => {
        const raw = snap.docs.map(d => ({ id: d.id, ...d.data() }))
        const now = Date.now()

        // Filter stale. A session with no readable createdAt is treated as STALE, not
        // fresh: the old `new Date(s.createdAt || now)` fallback gave an ageless doc an
        // age of zero, which is how hours-old invites slipped past the 60-minute bound.
        sessions.value = raw.filter(s => {
          const startedMs = sessionStartedMs(s)
          if (!startedMs) return false
          const diffMinutes = (now - startedMs) / 1000 / 60
          // Shorter stale threshold for ended calls
          if (s.status === 'ended' && diffMinutes > 1) return false
          // Standard timeout for stuck sessions
          if (diffMinutes > 60) return false
          return true
        })

        // Drive isRinging from Firestore state — no Twilio push dependency.
        // `ringsFor` folds both ringing scenarios (inbound, Invite-to-Call) into a
        // single `myRingingSession`, so the existing ringtone + OS notification +
        // CallPanel auto-expand pipeline fires for invites too — without that,
        // invitees got a silent Firestore-only signal and frequently missed the
        // invite entirely.
        const myUid = userStore.user?.uid
        const myRingingSession = sessions.value.find(s => ringsFor(s, myUid, now))
        const wasRinging = isRinging.value
        isRinging.value = !!myRingingSession

        // Fresh = a ring this subscription has not seen before. On the FIRST snapshot every
        // pre-existing ring is by definition old news unless it began seconds ago.
        let freshRing = false
        if (myRingingSession) {
          const ringKey = `${myRingingSession.id}|${myRingingSession.status}`
          const unseen = !ringKeysSeen.has(ringKey)
          ringKeysSeen.add(ringKey)
          const beganRecently = now - sessionStartedMs(myRingingSession) <= FRESH_ON_LOAD_MS
          freshRing = unseen && (sessionsFirstSnapshotSeen || beganRecently)
        }
        sessionsFirstSnapshotSeen = true

        // Ringtone: start on new ring, stop when ring ends
        if (myRingingSession && !wasRinging && freshRing) {
          playIncomingRingtone()
          fireBackgroundNotification(myRingingSession)
        } else if (!myRingingSession && wasRinging) {
          stopIncomingRingtone()
          closeIncomingNotification()
        }

        // Clear myActiveCallSid if the session ended or entered IVR timeout
        // (timeout_ivr means caller left conference — agent's connection is gone).
        // That holds only for a caller who was IN the room: their leg carries endConferenceOnExit. A caller who hung up
        // during the greeting, after this agent accepted, never joined, so this agent's leg is still connected to an
        // empty room: hang up our own leg and say why, instead of only dropping the panel (bug I0zL42wYNHOFn9dzELBc).
        if (myActiveCallSid.value) {
          const mySession = sessions.value.find(s => s.callSid === myActiveCallSid.value)
          if (!mySession || ['ended', 'timeout_ivr'].includes(mySession.status)) {
            if (mySession && mySession.status === 'ended' && callerNeverReachedRoom(mySession)) {
              leaveRoomCallerNeverReached(mySession.callSid)
            }
            answeredLegs.delete(myActiveCallSid.value)
            myActiveCallSid.value = null
          }
        }
      },
      error => {
        console.error('[CallStore] Snapshot Error:', error)
      }
    )
  }

  // -------------------------
  // Ringtone & Background Notification (Phase 48C)
  // -------------------------
  // Uses the same oscillator-through-AudioContext path that chat notifications
  // use (proven audible). Decoded mp3 buffer playback was inaudible in this
  // setup even though play() succeeded — likely Twilio Voice SDK contention
  // on the BufferSource sink. Oscillators bypass that.
  let stopRingtoneFn = null
  let activeIncomingNotification = null
  let alertPreferencesStore = null

  function ensureNotificationPermission() {
    if (typeof Notification === 'undefined') {
      console.log('[CallStore] Notifications API not supported')
      return
    }
    if (Notification.permission === 'default') {
      Notification.requestPermission()
        .then(perm => console.log('[CallStore] Notification permission:', perm))
        .catch(err =>
          console.warn('[CallStore] Notification permission request failed:', err?.message || err)
        )
    }
  }

  function getAlertPreferencesStore() {
    if (!alertPreferencesStore) {
      try {
        alertPreferencesStore = useAlertPreferencesStore()
      } catch (e) {
        console.warn('[CallStore] Alert preferences store not available:', e?.message || e)
      }
    }
    return alertPreferencesStore
  }

  function playIncomingRingtone() {
    if (stopRingtoneFn) {
      console.log('[CallStore] Ringtone already playing')
      return
    }
    const store = getAlertPreferencesStore()
    const soundEnabled = store?.isEventChannelEnabled?.('incomingCall', 'sound') ?? true
    if (!soundEnabled) {
      console.log('[CallStore] Ringtone suppressed by alert preferences')
      return
    }
    stopRingtoneFn = playRingtone()
    console.log('[CallStore] Ringtone started')
  }

  function stopIncomingRingtone() {
    if (!stopRingtoneFn) return
    try {
      stopRingtoneFn()
    } catch {
      /* noop */
    }
    stopRingtoneFn = null
    console.log('[CallStore] Ringtone stopped')
  }

  function fireBackgroundNotification(session) {
    // Fire on every ring (not just when document.hidden) so the OS chime
    // alerts the user even when the tab is foregrounded but AudioContext
    // is still suspended (no user gesture this page-load).
    const store = getAlertPreferencesStore()
    const overlayEnabled = store?.isEventChannelEnabled?.('incomingCall', 'overlay') ?? true
    if (!overlayEnabled) {
      console.log('[CallStore] OS notification suppressed by alert preferences')
      return
    }

    if (typeof Notification === 'undefined') return
    if (Notification.permission !== 'granted') {
      console.log('[CallStore] OS notification skipped, permission:', Notification.permission)
      return
    }

    // Close any prior call notification before raising a new one.
    if (activeIncomingNotification) {
      try {
        activeIncomingNotification.close()
      } catch {
        /* noop */
      }
      activeIncomingNotification = null
    }

    const callerName = session.customerName || session.fromNumber || 'Unknown Caller'
    try {
      activeIncomingNotification = new Notification('Incoming Call', {
        body: callerName,
        icon: '/favicon.ico',
        tag: `incoming-call-${session.callSid}`,
        silent: false,
      })
      activeIncomingNotification.onclick = () => {
        window.focus()
        if (activeIncomingNotification) {
          try {
            activeIncomingNotification.close()
          } catch {
            /* noop */
          }
          activeIncomingNotification = null
        }
      }
      console.log('[CallStore] OS notification fired')
    } catch (e) {
      console.warn('[CallStore] OS notification failed:', e?.message || e)
    }
  }

  function closeIncomingNotification() {
    if (!activeIncomingNotification) return
    try {
      activeIncomingNotification.close()
    } catch {
      /* noop */
    }
    activeIncomingNotification = null
  }

  // -------------------------
  // 4) Answer Call (Conference Model)
  // -------------------------
  // Hard ceiling on the answer flow. VoiceCallService has its own 15s watchdog
  // around device.connect(); this outer guard catches the failure mode where
  // either the backend claim or device.connect() silently never resolves and
  // would otherwise leave isConnecting stuck at true (locking the agent out
  // until refresh).
  const ANSWER_FLOW_TIMEOUT_MS = 25000

  function releaseClaimedSession(callSid) {
    if (!callSid) return
    const myUid = userStore.user?.uid
    if (!myUid) return
    // Transaction: only flip back to 'ringing' if WE still own the claim.
    // Without this guard, a stale catch path (e.g., our device.connect() failed
    // late) could clobber another agent's successful claim that landed in the
    // gap, kicking them off the call.
    runTransaction(db, async txn => {
      const ref = doc(db, 'activeCallSessions', callSid)
      const snap = await txn.get(ref)
      if (!snap.exists()) return
      const session = snap.data() || {}
      if (session.answeredByUid !== myUid) return
      txn.update(ref, {
        status: 'ringing',
        answeredByUid: null,
        answeredByName: null,
        connectedAt: null,
        updatedAt: serverTimestamp(),
      })
    }).catch(() => {})
  }

  // An inbound room call (browser channel) whose caller never joined the room: they hung up during the greeting.
  // Mirrors acceptedCallerNeverJoined in functions-comms/communications/voice/callOutcome.js; reached only for a call
  // this agent answered (myActiveCallSid).
  function callerNeverReachedRoom(session) {
    return (
      session?.channel === 'browser' &&
      !!session?.conferenceName &&
      !session?.participants?.customer?.joinedAt
    )
  }

  // Hang up OUR leg into the room the caller never reached (only the connection this call joined on, never another
  // call's), and tell the agent why. voiceStatusCallback also ends the room server-side; this covers the gap if that
  // lands late, and the toast is the agent's only explanation either way.
  // Called from the snapshot (the session ended while we were in the room) and from answerCall / grabCall (it ended
  // while we were still joining), so the toast is said once per call.
  const toldCallerLeft = new Set()
  function leaveRoomCallerNeverReached(callSid) {
    const leg = answeredLegs.get(callSid)
    if (leg && voiceCallService.currentConnection === leg) voiceCallService.hangup()
    answeredLegs.delete(callSid)
    if (toldCallerLeft.has(callSid)) return
    toldCallerLeft.add(callSid)
    eventBus.emit('toast', {
      message: 'The caller hung up before you were connected.',
      type: 'warning',
    })
  }

  // After our leg joins the room: register it, and leave at once if the caller already hung up during the greeting
  // while we were joining (the snapshot cannot hang up a leg that did not exist yet, and the server found no room).
  function settleAnsweredLeg(callSid) {
    answeredLegs.set(callSid, voiceCallService.currentConnection)
    const settled = sessions.value.find(s => s.callSid === callSid)
    if (settled?.status === 'ended' && callerNeverReachedRoom(settled)) {
      leaveRoomCallerNeverReached(callSid)
      if (myActiveCallSid.value === callSid) myActiveCallSid.value = null
      return false
    }
    return true
  }

  function endSessionFromAgentDisconnect(callSid) {
    const sessionDoc = sessions.value.find(s => s.callSid === callSid)
    const myUid = userStore.user?.uid
    answeredLegs.delete(callSid)
    // Never rewrite a session the server already ended: its endedReason / callOutcome say what happened (a caller who
    // hung up during the greeting is 'missed'), and this write would stamp 'completed' over them, with a late endedAt
    // if the tab was asleep (21:31Z for a call that ended 21:00Z on 2026-10-09; bug I0zL42wYNHOFn9dzELBc). The local
    // copy can be stale in a tab that slept, so the check is made on the server's own copy, in a transaction.
    if (myUid && sessionDoc?.id && sessionDoc.status !== 'ended') {
      const sessionRef = doc(db, 'activeCallSessions', sessionDoc.id)
      runTransaction(db, async tx => {
        const snap = await tx.get(sessionRef)
        if (!snap.exists() || snap.data()?.status === 'ended') return
        const session = snap.data()
        if (session.answeredByUid !== myUid) return
        // Leaving our leg is not ending a transferred call. Read the fresh
        // roster: an invite can arrive after this tab's last snapshot. Match
        // voiceStatus.shouldEndOnLeave: an agent joining/ringing counts, while
        // an outside participant must actually be connected. Ignore our own
        // entry because the provider's leave callback can arrive after ours.
        const otherCompany = Object.entries(session.participants || {}).some(([key, p]) => {
          if (key === `agent_${myUid}`) return false
          return (
            (key.startsWith('agent_') &&
              ['connected', 'connecting', 'ringing'].includes(p?.status)) ||
            (key.startsWith('ext_') && p?.status === 'connected')
          )
        })
        if (otherCompany) return
        tx.update(sessionRef, {
          status: 'ended',
          endedReason: 'completed',
          endedAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        })
      }).catch(() => {})
    }
    if (myActiveCallSid.value === callSid) myActiveCallSid.value = null
  }

  /**
   * Agent clicks Answer → claims call via backend transaction → joins conference via device.connect()
   */
  async function answerCall(callSid) {
    if (!callSid) return null

    // Double-click guard — if already connecting, reject immediately
    if (isConnecting.value) {
      throw new Error('Already connecting — please wait')
    }

    // Hardware pre-flight: if Twilio's Voice Device isn't registered, the
    // backend `connectAgentToCall` would succeed but `voiceCallService.connect()`
    // would fail almost instantly, our catch would release the claim back to
    // 'ringing', the user would re-click, and we'd burn through several
    // claim/release cycles in seconds. Refuse up-front instead.
    if (!voiceCallService.isDeviceReady.value) {
      throw new Error(
        'Voice client is not ready (another tab may have it, or your session needs a refresh).'
      )
    }

    // Lock BEFORE any await — the mic-permission prompt below can hold the
    // flow open for seconds, and an unlocked window there lets a second click
    // pass the guard and double-claim/double-connect (TOCTOU,
    // bugReports/vHVeHA6icxKduTZIPK0O). Reset on pre-flight failure so a
    // denied mic doesn't wedge the button.
    isConnecting.value = true

    // Mic pre-flight: Twilio Voice will happily connect a call with no audio
    // track, so a blocked-mic agent picks up to silent customers without any
    // error surfacing in-app. Refuse up-front and surface the fix path before
    // we claim the call — UNLESS the browser already reports the permission
    // granted, in which case the (100-500ms) mic acquisition runs in parallel
    // with the backend claim instead of stacking ahead of it. A granted-state
    // acquisition failing is rare (hardware), and the existing catch releases
    // the claim, so the refuse-before-claim hardening intent is preserved.
    let micAlreadyGranted = false
    try {
      try {
        const micStatus = await navigator.permissions?.query?.({ name: 'microphone' })
        micAlreadyGranted = micStatus?.state === 'granted'
      } catch {
        /* Permissions API unavailable (Safari) — keep the serial path */
      }
      if (!micAlreadyGranted) {
        await ensureMicrophonePermission()
      }
    } catch (err) {
      isConnecting.value = false
      throw err
    }

    let claimedCallSid = null
    let watchdog = null
    let watchdogFired = false

    const watchdogPromise = new Promise((_, reject) => {
      watchdog = setTimeout(() => {
        watchdogFired = true
        reject(new Error('Connection timed out — please try again'))
      }, ANSWER_FLOW_TIMEOUT_MS)
    })

    try {
      // 1. Stop ringtone immediately (instant feedback)
      stopIncomingRingtone()
      isRinging.value = false

      // 1b. Fast path: mic already granted → acquire it while the claim runs.
      // Park the rejection so it can't surface as unhandled while we await the
      // claim; the real await (with the watchdog) happens right after.
      let micPromise = null
      if (micAlreadyGranted) {
        micPromise = ensureMicrophonePermission()
        micPromise.catch(() => {})
      }

      // 2. Claim call via backend transaction (first agent wins)
      // The warm voiceCallPath route (docs/features/voice_call_path_FEATURE.md).
      const connectFn = liveCallCallable('connectAgentToCall')
      const result = await Promise.race([connectFn({ callSid }), watchdogPromise])
      const { conferenceName, customerId } = result.data
      claimedCallSid = callSid

      // 2b. Ensure the parallel mic acquisition actually succeeded before
      // joining the conference (failure here releases the claim in the catch).
      if (micPromise) {
        await Promise.race([micPromise, watchdogPromise])
      }

      // 3. Track which call we answered
      myActiveCallSid.value = callSid

      // 4. Join conference via outbound device.connect()
      await Promise.race([
        voiceCallService.connect(
          { To: `conference:${conferenceName}`, agentUid: userStore.user?.uid || '' },
          {
            onDisconnect: () => endSessionFromAgentDisconnect(callSid),
            onTimeout: () => {
              // VoiceCallService's internal watchdog tripped — connect() never
              // produced a useful Connection. Reset answer-side state so the
              // agent isn't locked out, and release the claim so another agent
              // can pick the call up.
              isConnecting.value = false
              myActiveCallSid.value = null
              releaseClaimedSession(callSid)
            },
            onError: err => {
              console.error('[CallStore] Twilio Connection error:', err)
              isConnecting.value = false
              myActiveCallSid.value = null
            },
          }
        ),
        watchdogPromise,
      ])

      // Connected — clear connecting state
      isConnecting.value = false
      // 5. Our leg is in the room: register it, and leave if the caller already hung up during the greeting.
      settleAnsweredLeg(callSid)
      return { callSid, customerId, conferenceName }
    } catch (e) {
      if (!watchdogFired) console.error('[CallStore] Failed to answer call:', e)
      isConnecting.value = false
      myActiveCallSid.value = null

      // If backend claim succeeded but device.connect() failed, release the session
      // so other agents can answer. Reset to 'ringing' with cleared answeredByUid.
      if (claimedCallSid) releaseClaimedSession(claimedCallSid)

      throw e
    } finally {
      if (watchdog) clearTimeout(watchdog)
    }
  }

  /**
   * GRAB a caller who is sitting in the timeout IVR ("Caller deciding…") —
   * interrupt the prompt and connect (docs/features/GRAB_DECIDING_CALL_FEATURE.md).
   *
   * Deliberately a SIBLING of answerCall rather than a flag on it: the
   * preconditions differ (timeout_ivr vs ringing) and so do the failure modes
   * (the caller can press 2 and reach voicemail while we're reaching for them).
   * Overloading the answer path is how subtle call-control bugs get made. The
   * hardening around it — device pre-flight, mic pre-flight, the isConnecting
   * TOCTOU lock, the watchdog, claim release on failure — is intentionally
   * mirrored, because every one of those exists for a reason answerCall already
   * learned the hard way.
   */
  async function grabCall(callSid) {
    if (!callSid) return null
    if (isConnecting.value) throw new Error('Already connecting — please wait')
    if (!voiceCallService.isDeviceReady.value) {
      throw new Error(
        'Voice client is not ready (another tab may have it, or your session needs a refresh).'
      )
    }

    isConnecting.value = true
    try {
      await ensureMicrophonePermission()
    } catch (err) {
      isConnecting.value = false
      throw err
    }

    let claimedCallSid = null
    let watchdog = null
    let watchdogFired = false
    const watchdogPromise = new Promise((_, reject) => {
      watchdog = setTimeout(() => {
        watchdogFired = true
        reject(new Error('Connection timed out — please try again'))
      }, ANSWER_FLOW_TIMEOUT_MS)
    })

    try {
      stopIncomingRingtone()

      const grabFn = httpsCallable(functions, 'grabDecidingCall')
      const result = await Promise.race([grabFn({ callSid }), watchdogPromise])
      const { conferenceName, customerId } = result.data
      claimedCallSid = callSid
      myActiveCallSid.value = callSid

      await Promise.race([
        voiceCallService.connect(
          { To: `conference:${conferenceName}`, agentUid: userStore.user?.uid || '' },
          {
            onDisconnect: () => endSessionFromAgentDisconnect(callSid),
            onTimeout: () => {
              isConnecting.value = false
              myActiveCallSid.value = null
              releaseClaimedSession(callSid)
            },
            onError: err => {
              console.error('[CallStore] Twilio Connection error (grab):', err)
              isConnecting.value = false
              myActiveCallSid.value = null
            },
          }
        ),
        watchdogPromise,
      ])

      isConnecting.value = false
      settleAnsweredLeg(callSid)
      return { callSid, customerId, conferenceName }
    } catch (e) {
      if (!watchdogFired) console.error('[CallStore] Failed to grab call:', e)
      isConnecting.value = false
      myActiveCallSid.value = null
      // The caller is already out of the IVR and in the conference at this
      // point, so releasing back to 'ringing' would be a lie. Release only
      // reverts OUR claim so another agent can take it.
      if (claimedCallSid) releaseClaimedSession(claimedCallSid)
      throw e
    } finally {
      if (watchdog) clearTimeout(watchdog)
    }
  }

  /**
   * Agent picks up a queued call → backend moves caller to conference → agent joins
   */
  async function answerQueueCall(callSid) {
    if (!callSid) return null
    if (isConnecting.value) throw new Error('Already connecting — please wait')
    if (!voiceCallService.isDeviceReady.value) {
      throw new Error(
        'Voice client is not ready (another tab may have it, or your session needs a refresh).'
      )
    }

    // Lock BEFORE the mic await — see answerCall (TOCTOU double-claim guard).
    isConnecting.value = true

    // Mirror the mic pre-flight in answerCall — same reason: blocked mic
    // produces silent calls without any in-app error.
    try {
      await ensureMicrophonePermission()
    } catch (err) {
      isConnecting.value = false
      throw err
    }

    let watchdog = null
    let watchdogFired = false

    const watchdogPromise = new Promise((_, reject) => {
      watchdog = setTimeout(() => {
        watchdogFired = true
        reject(new Error('Connection timed out — please try again'))
      }, ANSWER_FLOW_TIMEOUT_MS)
    })

    try {
      stopIncomingRingtone()
      isRinging.value = false

      const pickupFn = httpsCallable(functions, 'pickupQueueCall')
      const result = await Promise.race([pickupFn({ callSid }), watchdogPromise])
      const conferenceName = result.data?.conferenceName
      if (!conferenceName) throw new Error('No conference name returned')

      myActiveCallSid.value = callSid

      await Promise.race([
        voiceCallService.connect(
          { To: `conference:${conferenceName}`, agentUid: userStore.user?.uid || '' },
          {
            onDisconnect: () => endSessionFromAgentDisconnect(callSid),
            onTimeout: () => {
              isConnecting.value = false
              myActiveCallSid.value = null
            },
            onError: err => {
              console.error('[CallStore] Twilio Connection error:', err)
              isConnecting.value = false
              myActiveCallSid.value = null
            },
          }
        ),
        watchdogPromise,
      ])

      isConnecting.value = false
      return { callSid, conferenceName }
    } catch (e) {
      if (!watchdogFired) console.error('[CallStore] Failed to pick up queued call:', e)
      isConnecting.value = false
      myActiveCallSid.value = null
      throw e
    } finally {
      if (watchdog) clearTimeout(watchdog)
    }
  }

  function stop() {
    invitationIntents.clear()
    if (sessionUnsub) sessionUnsub()
    sessionUnsub = null

    if (heartbeatInterval.value) clearInterval(heartbeatInterval.value)
    heartbeatInterval.value = null
    if (removeWakeListeners) removeWakeListeners()

    stopIncomingRingtone()
    myActiveCallSid.value = null
    isConnecting.value = false
    isRinging.value = false
    sessions.value = []
    // Reset the schedule-boundary tracker so the next login runs the
    // initial-load enforcement branch again. The Pinia singleton survives SPA
    // logout/login without a page reload — a stale value here skipped the
    // on-load auto-toggle and left agents Available outside their window
    // (bugReports/iBsqe1VMiQVjNVIYSYXH).
    wasInWindow = null
  }

  // ─── Invite-to-Call helpers ─────────────────────────────────────────────
  // Any call the current user is "ringing" on in the participants roster is
  // an outstanding invitation. The UI shows one banner per pending invite.
  const pendingInvites = computed(() => {
    const uid = userStore.user?.uid
    if (!uid) return []
    return sessions.value
      .filter(s => s.status === 'connected')
      .filter(s => s.participants?.[`agent_${uid}`]?.status === 'ringing')
  })

  async function inviteToCall({ callSid, targetUid, targetPhone, targetLabel }) {
    console.log('[CallStore] inviteToCall called', { callSid, targetUid, targetPhone, targetLabel })
    if (!callSid) throw new Error('Missing callSid')
    // The warm voiceCallPath route (docs/features/voice_call_path_FEATURE.md).
    const fn = liveCallCallable('inviteToCall')
    const intent = invitationIntent('send', callSid, targetUid || targetPhone || '')
    try {
      const res = await fn({
        callSid,
        targetUid,
        targetPhone,
        targetLabel,
        invitationOperationId: intent.id,
      })
      invitationIntents.delete(intent.key)
      console.log('[CallStore] inviteToCall succeeded', res.data)
      return res.data
    } catch (err) {
      console.error('[CallStore] inviteToCall callable error', err?.code, err?.message, err)
      throw err
    }
  }

  // Counterpart to inviteToCall — drop a live participant. Exists because a
  // member's voicemail can answer a group call and there was previously no way
  // to evict it.
  async function removeCallParticipant({ callSid, participantKey }) {
    if (!callSid) throw new Error('Missing callSid')
    if (!participantKey) throw new Error('Missing participantKey')
    const fn = httpsCallable(functions, 'removeCallParticipant')
    const intent = invitationIntent('remove', callSid, participantKey)
    try {
      const res = await fn({ callSid, participantKey, invitationOperationId: intent.id })
      invitationIntents.delete(intent.key)
      return res.data
    } catch (err) {
      console.error('[CallStore] removeCallParticipant error', err?.code, err?.message)
      throw err
    }
  }

  // Put the caller on hold / take them off it (bug rXTNmlfmycTvYm2xp1BS). The server holds the caller's leg and
  // writes `callerHold` on the session, which reaches every agent's panel through the sessions listener.
  async function setCallerHold({ callSid, hold }) {
    if (!callSid) throw new Error('Missing callSid')
    const fn = httpsCallable(functions, 'setCallerHold')
    try {
      const res = await fn({ callSid, hold: !!hold })
      return res.data
    } catch (err) {
      console.error('[CallStore] setCallerHold error', err?.code, err?.message)
      throw err
    }
  }

  async function acceptCallInvite(callSid) {
    if (!callSid) throw new Error('Missing callSid')
    if (isConnecting.value) throw new Error('Already connecting — please wait')
    const intent = invitationIntent('accept', callSid)
    if (!voiceCallService.isDeviceReady.value) {
      throw new Error(
        'Voice client is not ready (another tab may have it, or your session needs a refresh).'
      )
    }

    // Lock BEFORE the mic await — see answerCall (TOCTOU double-claim guard).
    isConnecting.value = true

    // Stop ringing the moment Accept is clicked, as answerCall does. Waiting for the
    // backend meant a cold acceptCallInvite rang on for ~4 s after the click
    // (Evelina, 2026-09-28). Joining a call silences every ring, as answering does.
    stopIncomingRingtone()
    isRinging.value = false

    // Mic pre-flight — see answerCall for rationale.
    try {
      await ensureMicrophonePermission()
    } catch (err) {
      isConnecting.value = false
      throw err
    }

    let watchdog = null
    let watchdogFired = false
    const watchdogPromise = new Promise((_, reject) => {
      watchdog = setTimeout(() => {
        watchdogFired = true
        reject(new Error('Connection timed out — please try again'))
      }, ANSWER_FLOW_TIMEOUT_MS)
    })

    try {
      const fn = liveCallCallable('acceptCallInvite')
      const res = await Promise.race([fn({ callSid, acceptanceId: intent.id }), watchdogPromise])
      if (userStore.user?.uid !== intent.uid)
        throw new Error('Your sign-in changed before the call connected')
      const joinParams = companyInvitationJoinParams(res.data, {
        callSid,
        uid: intent.uid,
        acceptanceId: intent.id,
      })

      myActiveCallSid.value = callSid

      await Promise.race([
        voiceCallService.connect(joinParams, {
          onDisconnect: () => {
            invitationIntents.delete(intent.key)
            myActiveCallSid.value = null
          },
          onTimeout: () => {
            isConnecting.value = false
            myActiveCallSid.value = null
          },
          onError: err => {
            console.error('[CallStore] acceptCallInvite Connection error:', err)
            isConnecting.value = false
            myActiveCallSid.value = null
          },
        }),
        watchdogPromise,
      ])

      isConnecting.value = false
      invitationIntents.delete(intent.key)
      return res.data
    } catch (err) {
      if (!watchdogFired) console.error('[CallStore] acceptCallInvite failed:', err)
      isConnecting.value = false
      myActiveCallSid.value = null
      throw err
    } finally {
      if (watchdog) clearTimeout(watchdog)
    }
  }

  async function declineCallInvite(callSid) {
    if (!callSid) throw new Error('Missing callSid')
    // Stop ringing the moment Decline is clicked, instead of when the backend's write
    // comes back through the snapshot — unless another call is still ringing for
    // this person: declining one invite must not silence a different ring.
    const myUid = userStore.user?.uid
    const now = Date.now()
    const otherRinging = sessions.value.some(
      s => (s.callSid || s.id) !== callSid && ringsFor(s, myUid, now)
    )
    if (!otherRinging) {
      stopIncomingRingtone()
      isRinging.value = false
    }
    const fn = liveCallCallable('declineCallInvite')
    const res = await fn({ callSid })
    invitationIntents.delete(JSON.stringify(['accept', myUid, callSid, '']))
    return res.data
  }

  /**
   * Ask the Call Center to call this number now. Returns false when there is nothing to dial. A prospect's call
   * (Phase 97 S4d) names the prospect: it is judged and logged as theirs.
   */
  function requestDial({ phone, customerId = null, prospectId = null, customerName = null } = {}) {
    if (!phone) return false
    dialRequest.value = {
      phone,
      customerId,
      prospectId,
      customerName,
      nonce: `${Date.now()}-${Math.random()}`,
    }
    return true
  }
  /** The Call Center takes a request exactly once. */
  function takeDialRequest(nonce) {
    const req = dialRequest.value
    if (!req || req.nonce !== nonce) return null
    dialRequest.value = null
    return req
  }

  async function cancelCallInvite({ callSid, targetUid, targetKey, reason }) {
    if (!callSid) throw new Error('Missing callSid')
    if (!targetUid && !targetKey) throw new Error('Missing targetUid or targetKey')
    const fn = liveCallCallable('cancelCallInvite')
    const res = await fn({ callSid, targetUid, targetKey, reason })
    return res.data
  }

  return {
    isAvailable,
    isConnecting,
    sessions,
    waitingQueue,
    activeCalls,
    activeCallSession,
    myActiveCall,
    myActiveCallSid,
    dialRequest,
    requestDial,
    takeDialRequest,
    isRinging,
    callStatus,
    callerInfo,
    pendingInvites,
    initPresence,
    toggleAvailability,
    subscribeToCallSessions,
    answerCall,
    grabCall,
    answerQueueCall,
    inviteToCall,
    removeCallParticipant,
    setCallerHold,
    acceptCallInvite,
    declineCallInvite,
    cancelCallInvite,
    stopIncomingRingtone,
    stop,
  }
})
